import { motion } from 'framer-motion';
import { Award, Briefcase, GraduationCap, Target, Users, Globe, Lightbulb, TrendingUp } from 'lucide-react';
import { BrandDot } from '../components/brand/BrandMotif';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};

const careerHighlights = [
  {
    icon: Briefcase,
    title: 'Telecom Industry Leadership',
    description: 'Nearly three decades of transformative experience driving business growth, profitability, and market penetration across the telecommunications sector.',
  },
  {
    icon: Globe,
    title: 'Cross-Industry Expertise',
    description: 'Extensive experience across Automobiles, Real Estate, Telecommunications, Retail, BFSI, State Governments, Public Utilities, E-commerce, MeS, IT & ITeS verticals.',
  },
  {
    icon: Users,
    title: 'Cross-Cultural Team Building',
    description: 'Proven track record of building and leading diverse, high-performing teams that consistently exceed targets and deliver exceptional results.',
  },
  {
    icon: Target,
    title: 'Business Operations Excellence',
    description: 'Strategic leadership in determining business unit mission, formulating tactical initiatives, and implementing strategic advice for achieving corporate objectives.',
  },
];

const expertiseAreas = [
  'Transmission Technologies & Optic Fibre',
  'Enterprise B2B Sales',
  'Construction Technology',
  'Telecom Infrastructure',
  'Brand Management',
  'Business Development',
  'Digitalisation',
  'Content Delivery',
  'Enterprise Voice & Cloud',
  'Unified Communications',
  'IoT & Smart Cities',
];

export const FounderPage = () => {
  return (
    <div data-testid="founder-page">
      {/* Hero Banner - 1920 × 420px slim banner */}
      <section 
        className="relative flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 30, 45, 0.4), rgba(11, 30, 45, 0.5)), url('/assets/banners/Founder_page_banner.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '420px',
          height: '420px',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 w-full">
          <motion.div 
            className="max-w-2xl"
            initial="initial"
            animate="animate"
            variants={{
              animate: { transition: { staggerChildren: 0.1 } }
            }}
          >
            <motion.span 
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#4DB848] mb-4 block"
            >
              Meet the Founder
            </motion.span>
            <motion.h1 
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-montserrat font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4"
            >
              S.B. Shekhar Rao
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="font-inter text-base md:text-lg text-slate-300 leading-relaxed"
            >
              Founder & Principal Strategist, SGA STRAT
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Founder Profile Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* Profile Image */}
            <motion.div 
              className="lg:col-span-2 flex flex-col items-center"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative max-w-[280px]">
                <div className="absolute -inset-3 bg-gradient-to-br from-[#0087B4]/20 to-[#4DB848]/20 blur-2xl" />
                <div className="relative border-4 border-white shadow-2xl overflow-hidden">
                  <img
                    src="/assets/founder/shekhar-raov2.jpg"
                    alt="S.B. Shekhar Rao - Founder of SGA STRAT"
                    className="w-full h-auto object-cover"
                    data-testid="founder-image"
                  />
                </div>
                {/* Decorative brand motif */}
                <div className="flex gap-1 mt-6 justify-center">
                  {[2, 2.5, 3, 3.5, 4, 3.5, 3].map((size, i) => (
                    <motion.span 
                      key={i}
                      className="rounded-full bg-[#4DB848]"
                      style={{ width: size * 3, height: size * 3 }}
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.3, delay: i * 0.05 }}
                    />
                  ))}
                </div>
              </div>
              
              {/* LinkedIn Profile Link */}
              <motion.div 
                className="mt-8 text-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <a
                  href="https://www.linkedin.com/in/s-b-shekhar-rao-b4b9562/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#0077B5] text-white font-inter font-medium text-sm hover:bg-[#005885] transition-colors duration-300"
                  data-testid="linkedin-link"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  Connect on LinkedIn
                </a>
              </motion.div>
            </motion.div>

            {/* Biography Content */}
            <motion.div 
              className="lg:col-span-3"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
                About the Founder
              </span>
              <h2 className="font-montserrat font-bold text-2xl md:text-3xl text-[#0B1E2D] mb-6">
                29 Years of Strategic Leadership
              </h2>
              
              <div className="space-y-5 font-inter text-slate-600 leading-relaxed">
                <p>
                  S.B. Shekhar Rao is an accomplished professional with 29 years of career success in driving business growth, increasing profitability, and expanding market penetration across the telecom industry. A thought leader who stays at the forefront of business and technology innovation through continuous learning and industry engagement.
                </p>
                
                <p>
                  Throughout his distinguished career, his dexterity has been demonstrated in consistently achieving targets, building cross-cultural teams, and identifying high-yielding services and products. He has developed and refined his skills in facilitating sales across all industry verticals, including Automobiles, Real Estate, Telecommunications, Retail, Partners, BFSI, State Governments, Public Utilities, E-commerce, MeS, IT, & ITeS.
                </p>
                
                <p>
                  Over the years, Shekhar has established his presence in key technology domains including Transmission Technologies & Optic Fibre, Enterprise B2B Sales, Construction Technology, Telecom Infrastructure, Brand Management, Business Development, Digitalisation, Content Delivery, Enterprise Voice & Cloud, Unified Communications, IoT & Smart Cities.
                </p>

                <p>
                  As a Business Operations Point of Contact, he has successfully determined the mission and direction of business units, demonstrating the ability to formulate and implement tactical initiatives as well as provide strategic advice for achieving corporate goals and objectives.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Career Highlights */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
              Career Highlights
            </span>
            <h2 className="font-montserrat font-bold text-2xl md:text-3xl text-[#0B1E2D]">
              Leadership & Expertise
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {careerHighlights.map((highlight, index) => (
              <motion.div
                key={highlight.title}
                data-testid={`highlight-card-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 p-8 hover:border-[#0087B4]/30"
              >
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 flex-shrink-0 bg-[#4DB848]/10 flex items-center justify-center">
                    <highlight.icon className="w-7 h-7 text-[#4DB848]" />
                  </div>
                  <div>
                    <h3 className="font-montserrat font-bold text-lg text-[#0B1E2D] mb-2">
                      {highlight.title}
                    </h3>
                    <p className="font-inter text-slate-600 leading-relaxed">
                      {highlight.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas of Expertise */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#0087B4] mb-4 block">
                Domain Expertise
              </span>
              <h2 className="font-montserrat font-bold text-2xl md:text-3xl text-[#0B1E2D] mb-6">
                Areas of Specialization
              </h2>
              <p className="font-inter text-slate-600 leading-relaxed mb-8">
                With deep expertise spanning multiple technology and business domains, 
                Shekhar brings a unique combination of strategic vision and operational 
                excellence to every engagement.
              </p>
            </div>
            
            <div className="flex flex-wrap gap-3">
              {expertiseAreas.map((area, index) => (
                <motion.span
                  key={area}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="px-4 py-2 bg-[#0087B4] text-white font-inter text-sm font-medium"
                  data-testid={`expertise-tag-${index}`}
                >
                  {area}
                </motion.span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision for SGA STRAT */}
      <section className="py-20 bg-gradient-to-br from-[#0B1E2D] to-[#0F2A3D]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <Lightbulb className="w-8 h-8 text-[#4DB848]" />
                <span className="font-montserrat font-semibold text-sm uppercase tracking-widest text-[#4DB848]">
                  The Genesis of SGA STRAT
                </span>
              </div>
              <h2 className="font-montserrat font-bold text-2xl md:text-3xl text-white mb-6">
                Why SGA STRAT Was Born
              </h2>
              <div className="space-y-5 font-inter text-slate-300 leading-relaxed">
                <p>
                  After nearly three decades of navigating complex business transformations and witnessing firsthand the challenges organizations face in bridging strategy with execution, Shekhar recognized a critical gap in the market—the need for a consulting partner that truly understands both the strategic vision and the operational realities of transformation.
                </p>
                <p>
                  SGA STRAT was founded with a singular mission: to leverage decades of cross-industry experience, deep domain expertise, and proven leadership capabilities to help organizations transform confidently. The firm embodies Shekhar's belief that sustainable business transformation requires not just strategic insight, but the operational wisdom to execute effectively.
                </p>
                <p>
                  Drawing from his extensive experience across telecom infrastructure, digital transformation, enterprise sales, and technology ecosystems, Shekhar created SGA STRAT as a vehicle to share his expertise with organizations seeking a trusted transformation partner—one who has walked the path they're embarking upon.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-6 h-6 text-[#4DB848]" />
                <h3 className="font-montserrat font-bold text-xl text-white">
                  Core Philosophy
                </h3>
              </div>
              <ul className="space-y-4">
                {[
                  'Strategy without execution is merely aspiration',
                  'Transformation succeeds when vision meets operational reality',
                  'Cross-cultural collaboration unlocks unprecedented value',
                  'Technology enables transformation; people drive it',
                  'Sustainable growth emerges from strategic discipline',
                ].map((philosophy, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <BrandDot size="sm" className="mt-1.5 flex-shrink-0" />
                    <span className="font-inter text-slate-300">{philosophy}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-montserrat font-bold text-2xl md:text-3xl text-[#0B1E2D] mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="font-inter text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Connect with Shekhar and the SGA STRAT team to explore how our strategic 
            expertise can accelerate your transformation journey.
          </p>
          <a
            href="/engage"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#0087B4] text-white font-montserrat font-semibold text-sm uppercase tracking-wider hover:bg-[#006d91] transition-colors duration-300"
            data-testid="engage-cta"
          >
            Get In Touch
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </section>
    </div>
  );
};
